import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Stack,
  Group,
  Button,
  Select,
  TextInput,
  NumberInput,
  Checkbox,
  Badge,
  Tabs,
  Text,
  Paper,
  Table,
  ScrollArea,
  Tooltip,
  Modal,
  SegmentedControl,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconArrowLeft,
  IconAlertTriangle,
  IconFilter,
} from '@tabler/icons-react';
import type { ImportWizardState, ImportRow, AutoFixResult } from '../types';
import { processImportRows, computeRowIssues } from '../utils/matchingEngine';
import {
  buildBulkRequest,
  type BulkImportRequest,
  type PreflightDuplicate,
  type PreflightResponse,
} from '../hooks/useBulkImport';
import {
  CORAL_CATEGORIES,
  CORAL_CATEGORY_OPTIONS,
  getSubcategoriesForCategory,
} from '@/data/coralCategories';
import { STATUS_OPTIONS } from '@/types/livestock';
import { getIncompleteRowSummary } from '../utils/incompleteRowCheck';

type DuplicateDecision = 'skip' | 'import';

interface ReviewStepProps {
  state: ImportWizardState;
  onUpdate: (updates: Partial<ImportWizardState>) => void;
  onImport: (request: BulkImportRequest) => void;
  onPreflight: (request: BulkImportRequest) => Promise<PreflightResponse>;
  onBack: () => void;
  isImporting?: boolean;
}

type FilterTab = 'all' | 'issues' | 'duplicates';

// Status options for the dropdown
const STATUS_SELECT_OPTIONS = STATUS_OPTIONS.map((s) => ({
  value: s.value,
  label: s.label,
}));

/**
 * Render a small "was: X" label under auto-corrected cells.
 */
function AutoFixLabel({ result }: { result: AutoFixResult | null }) {
  if (!result?.originalValue) return null;
  return (
    <Text size="xs" c="dimmed" mt={2}>
      was: {result.originalValue}
    </Text>
  );
}

// No Date conversion helpers needed — Mantine v8 DateInput works with
// string values (DateStringValue = string) in YYYY-MM-DD format.

export function ReviewStep({
  state,
  onUpdate,
  onImport,
  onPreflight,
  onBack,
  isImporting,
}: ReviewStepProps) {
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const { importRows, livestockType, currency } = state;

  const isCoral = livestockType === 'coral';

  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
  const [incompleteSummary, setIncompleteSummary] = useState<ReturnType<typeof getIncompleteRowSummary>>(null);

  // Duplicate resolution state
  const [preflightDuplicates, setPreflightDuplicates] = useState<PreflightDuplicate[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<BulkImportRequest | null>(null);
  const [duplicateDecisions, setDuplicateDecisions] = useState<Record<number, DuplicateDecision>>({});

  /**
   * Run the preflight check and either proceed directly or show the
   * duplicate resolution modal.
   */
  const proceedToImport = useCallback(async () => {
    const request = buildBulkRequest(importRows, livestockType, currency);
    try {
      const preflight = await onPreflight(request);
      if (preflight.duplicate_count > 0) {
        setPreflightDuplicates(preflight.duplicates);
        setPendingRequest(request);
        // Default all decisions to 'skip'
        const defaults: Record<number, DuplicateDecision> = {};
        preflight.duplicates.forEach((d) => {
          defaults[d.index] = 'skip';
        });
        setDuplicateDecisions(defaults);
        setShowDuplicateModal(true);
      } else {
        onImport(request);
      }
    } catch {
      // Error already shown via useBulkPreflight onError notification
    }
  }, [importRows, livestockType, currency, onPreflight, onImport]);

  // Intercept the import button — check for incomplete rows first
  const handleImportClick = useCallback(() => {
    const summary = getIncompleteRowSummary(importRows);
    if (summary) {
      setIncompleteSummary(summary);
      setShowIncompleteWarning(true);
    } else {
      proceedToImport();
    }
  }, [importRows, proceedToImport]);

  const handleConfirmImport = useCallback(() => {
    setShowIncompleteWarning(false);
    proceedToImport();
  }, [proceedToImport]);

  /**
   * Resolve duplicates: send the FULL request with all items plus explicit
   * duplicate_decisions so the backend can skip/import based on user choices.
   * We do NOT filter items out because that would change indices and break
   * the duplicate_decisions mapping.
   */
  const handleDuplicateResolution = useCallback(() => {
    if (!pendingRequest) return;

    // Build duplicate decisions from user's choices
    const duplicate_decisions = preflightDuplicates.map((dup) => ({
      index: dup.index,
      action: (duplicateDecisions[dup.index] === 'skip' ? 'skip' : 'import') as 'skip' | 'import',
    }));

    // Send the FULL request with all items + explicit decisions
    const requestWithDecisions: BulkImportRequest = {
      ...pendingRequest,
      duplicate_decisions,
    };

    setShowDuplicateModal(false);
    onImport(requestWithDecisions);
  }, [pendingRequest, preflightDuplicates, duplicateDecisions, onImport]);

  const importAnywayCount = useMemo(
    () =>
      preflightDuplicates.filter(
        (d) => duplicateDecisions[d.index] === 'import',
      ).length,
    [preflightDuplicates, duplicateDecisions],
  );

  const skipCount = useMemo(
    () => preflightDuplicates.length - importAnywayCount,
    [preflightDuplicates.length, importAnywayCount],
  );

  const totalAfterResolution = useMemo(() => {
    if (!pendingRequest) return 0;
    return pendingRequest.items.length - skipCount;
  }, [pendingRequest, skipCount]);

  // Run the matching engine on mount (when we first arrive at this step)
  useEffect(() => {
    if (
      importRows.length === 0 &&
      state.parsedData &&
      state.columnMappings.length > 0
    ) {
      const processed = processImportRows(
        state.parsedData.rows,
        state.columnMappings,
        state.dateFormat,
      );
      onUpdate({ importRows: processed });
    }
  }, [
    importRows.length,
    state.parsedData,
    state.columnMappings,
    state.dateFormat,
    onUpdate,
  ]);

  // Update a single row in the importRows array
  const updateRow = useCallback(
    (rowIndex: number, updates: Partial<ImportRow>) => {
      onUpdate({
        importRows: importRows.map((row) =>
          row._rowIndex === rowIndex ? { ...row, ...updates } : row,
        ),
        hasReviewEdits: true,
      });
    },
    [importRows, onUpdate],
  );

  // Filtered rows based on active tab (using live issue computation)
  const filteredRows = useMemo(() => {
    switch (activeFilter) {
      case 'issues':
        return importRows.filter((r) => computeRowIssues(r).length > 0);
      case 'duplicates':
        return importRows.filter((r) => r.isDuplicate);
      default:
        return importRows;
    }
  }, [importRows, activeFilter]);

  // Counts for tabs and bottom bar (using live issue computation)
  const counts = useMemo(() => {
    const total = importRows.length;
    const withIssues = importRows.filter((r) => computeRowIssues(r).length > 0).length;
    const duplicates = importRows.filter((r) => r.isDuplicate).length;
    const skipped = importRows.filter((r) => r.isSkipped).length;
    const toImport = total - skipped;
    return { total, withIssues, duplicates, skipped, toImport };
  }, [importRows]);

  // Determine which columns to show based on mapped fields
  const hasColumn = useMemo(() => {
    const mapped = new Set(state.columnMappings.map((m) => m.targetField));
    return {
      name: true, // always shown
      category: isCoral && mapped.has('category'),
      subcategory: isCoral && mapped.has('subcategory'),
      status: mapped.has('status'),
      price: mapped.has('price'),
      date: mapped.has('acquisition_date'),
      vendor: mapped.has('vendor'),
      notes: mapped.has('notes'),
      scientificName: mapped.has('scientific_name'),
    };
  }, [state.columnMappings, isCoral]);

  if (importRows.length === 0) {
    return (
      <Stack gap="lg" mt="lg">
        <Text c="dimmed" ta="center" py="xl">
          Processing rows...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" mt="lg">
      <Text size="sm" c="dimmed">
        Review and edit your data before importing. Auto-corrected values show
        the original value below. Flagged values need your attention.
      </Text>

      {/* Filter tabs */}
      <Tabs
        value={activeFilter}
        onChange={(v) => setActiveFilter((v as FilterTab) ?? 'all')}
      >
        <Tabs.List>
          <Tabs.Tab value="all" leftSection={<IconFilter size={14} />}>
            All ({counts.total})
          </Tabs.Tab>
          <Tabs.Tab
            value="issues"
            leftSection={<IconAlertTriangle size={14} />}
            color={counts.withIssues > 0 ? 'yellow' : undefined}
          >
            Issues ({counts.withIssues})
          </Tabs.Tab>
          <Tabs.Tab value="duplicates">
            Duplicates ({counts.duplicates})
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {/* Editable grid */}
      <Paper withBorder>
        <ScrollArea>
          <Table highlightOnHover style={{ minWidth: 900 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>
                  <Text size="xs" fw={600}>
                    Skip
                  </Text>
                </Table.Th>
                <Table.Th style={{ width: 30 }}>
                  <Text size="xs" fw={600}>
                    #
                  </Text>
                </Table.Th>
                <Table.Th style={{ minWidth: 160 }}>Name</Table.Th>
                {hasColumn.category && (
                  <Table.Th style={{ minWidth: 150 }}>Category</Table.Th>
                )}
                {hasColumn.subcategory && (
                  <Table.Th style={{ minWidth: 150 }}>Subcategory</Table.Th>
                )}
                {hasColumn.status && (
                  <Table.Th style={{ minWidth: 130 }}>Status</Table.Th>
                )}
                {hasColumn.price && (
                  <Table.Th style={{ minWidth: 100 }}>Price</Table.Th>
                )}
                {hasColumn.date && (
                  <Table.Th style={{ minWidth: 150 }}>Date</Table.Th>
                )}
                {hasColumn.vendor && (
                  <Table.Th style={{ minWidth: 130 }}>Vendor</Table.Th>
                )}
                {hasColumn.scientificName && (
                  <Table.Th style={{ minWidth: 150 }}>
                    Scientific Name
                  </Table.Th>
                )}
                {hasColumn.notes && (
                  <Table.Th style={{ minWidth: 160 }}>Notes</Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredRows.map((row) => (
                <ReviewRow
                  key={row._rowIndex}
                  row={row}
                  hasColumn={hasColumn}
                  onUpdate={updateRow}
                  isCoral={isCoral}
                />
              ))}
              {filteredRows.length === 0 && (
                <Table.Tr>
                  <Table.Td
                    colSpan={10}
                    style={{ textAlign: 'center', padding: '2rem' }}
                  >
                    <Text c="dimmed">
                      {activeFilter === 'issues'
                        ? 'No rows with issues'
                        : activeFilter === 'duplicates'
                          ? 'No duplicate rows detected'
                          : 'No rows to display'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* Bottom bar with count and navigation */}
      <Paper withBorder p="md">
        <Group justify="space-between">
          <Group gap="xs">
            <Text size="sm" fw={500}>
              Importing {counts.toImport} of {counts.total} rows
            </Text>
            {counts.skipped > 0 && (
              <Badge variant="light" color="gray" size="sm">
                {counts.skipped} skipped
              </Badge>
            )}
            {counts.duplicates > 0 && (
              <Badge variant="light" color="yellow" size="sm">
                {counts.duplicates} duplicates
              </Badge>
            )}
          </Group>
          <Group gap="md">
            <Button
              variant="default"
              onClick={onBack}
              leftSection={<IconArrowLeft size={16} />}
            >
              Back
            </Button>
            <Button
              onClick={handleImportClick}
              disabled={counts.toImport === 0}
              loading={isImporting}
              size="md"
            >
              Import {counts.toImport}{' '}
              {livestockType.charAt(0).toUpperCase() + livestockType.slice(1)}
              {counts.toImport !== 1 ? 's' : ''}
            </Button>
          </Group>
        </Group>
      </Paper>

      <Modal
        opened={showIncompleteWarning}
        onClose={() => setShowIncompleteWarning(false)}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={20} color="var(--mantine-color-yellow-6)" />
            <Text fw={600}>Incomplete rows detected</Text>
          </Group>
        }
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            {incompleteSummary?.totalIncomplete}{' '}
            {incompleteSummary?.totalIncomplete === 1 ? 'item has' : 'items have'}{' '}
            missing fields:
          </Text>
          <Stack gap={4}>
            {(incompleteSummary?.withIssues ?? 0) > 0 && (
              <Text size="sm" c="dimmed">
                {incompleteSummary!.withIssues}{' '}
                {incompleteSummary!.withIssues === 1 ? 'row has' : 'rows have'}{' '}
                unresolved issues (missing or unrecognised fields)
              </Text>
            )}
            {(incompleteSummary?.missingAcquisitionDate ?? 0) > 0 && (
              <Text size="sm" c="dimmed">
                {incompleteSummary!.missingAcquisitionDate}{' '}
                {incompleteSummary!.missingAcquisitionDate === 1 ? 'item has' : 'items have'}{' '}
                no date &mdash; will default to today ({new Date().toISOString().slice(0, 10)})
              </Text>
            )}
            {(incompleteSummary?.missingStatus ?? 0) > 0 && (
              <Text size="sm" c="dimmed">
                {incompleteSummary!.missingStatus}{' '}
                {incompleteSummary!.missingStatus === 1 ? 'item has' : 'items have'}{' '}
                no status &mdash; will default to alive
              </Text>
            )}
          </Stack>
          {((incompleteSummary?.missingAcquisitionDate ?? 0) > 0 ||
            (incompleteSummary?.missingStatus ?? 0) > 0) && (
            <Text size="xs" c="dimmed" fs="italic">
              These can be edited after import.
            </Text>
          )}
          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => setShowIncompleteWarning(false)}
            >
              Go back and fix
            </Button>
            <Button
              color="yellow"
              onClick={handleConfirmImport}
            >
              Import anyway
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Duplicate resolution modal */}
      <Modal
        opened={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={20} color="var(--mantine-color-yellow-6)" />
            <Text fw={600}>
              {preflightDuplicates.length} potential{' '}
              {preflightDuplicates.length === 1 ? 'duplicate' : 'duplicates'}{' '}
              found
            </Text>
          </Group>
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            These items match existing records in your tank. Choose whether to
            skip or import each one.
          </Text>

          {/* Apply-to-all shortcuts */}
          <Group gap="xs">
            <Button
              variant="light"
              size="xs"
              onClick={() => {
                const all: Record<number, DuplicateDecision> = {};
                preflightDuplicates.forEach((d) => {
                  all[d.index] = 'skip';
                });
                setDuplicateDecisions(all);
              }}
            >
              Skip all duplicates
            </Button>
            <Button
              variant="light"
              size="xs"
              onClick={() => {
                const all: Record<number, DuplicateDecision> = {};
                preflightDuplicates.forEach((d) => {
                  all[d.index] = 'import';
                });
                setDuplicateDecisions(all);
              }}
            >
              Import all anyway
            </Button>
          </Group>

          <ScrollArea.Autosize mah={400}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Incoming item</Table.Th>
                  <Table.Th>Existing match</Table.Th>
                  <Table.Th style={{ width: 160 }}>Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preflightDuplicates.map((dup) => (
                  <Table.Tr key={dup.index}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {dup.incoming.common_name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {[dup.incoming.vendor, dup.incoming.acquisition_date]
                          .filter(Boolean)
                          .join(' | ')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {dup.existing.common_name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {[
                          dup.existing.vendor,
                          dup.existing.acquisition_date,
                          dup.existing.status,
                        ]
                          .filter(Boolean)
                          .join(' | ')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <SegmentedControl
                        size="xs"
                        value={duplicateDecisions[dup.index] ?? 'skip'}
                        onChange={(val) =>
                          setDuplicateDecisions((prev) => ({
                            ...prev,
                            [dup.index]: val as DuplicateDecision,
                          }))
                        }
                        data={[
                          { label: 'Skip', value: 'skip' },
                          { label: 'Import', value: 'import' },
                        ]}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>

          <Group justify="space-between" mt="md">
            <Text size="sm" c="dimmed">
              {totalAfterResolution > 0
                ? `${totalAfterResolution} item${totalAfterResolution !== 1 ? 's' : ''} will be imported`
                : 'All items will be skipped'}
              {skipCount > 0 &&
                ` (${skipCount} duplicate${skipCount !== 1 ? 's' : ''} skipped)`}
            </Text>
            <Group gap="md">
              <Button
                variant="default"
                onClick={() => setShowDuplicateModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDuplicateResolution}
                disabled={totalAfterResolution === 0}
              >
                {totalAfterResolution > 0
                  ? `Import ${totalAfterResolution} item${totalAfterResolution !== 1 ? 's' : ''}`
                  : 'Nothing to import'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Individual row component — extracted for clarity
// ---------------------------------------------------------------------------

interface ReviewRowProps {
  row: ImportRow;
  hasColumn: {
    name: boolean;
    category: boolean;
    subcategory: boolean;
    status: boolean;
    price: boolean;
    date: boolean;
    vendor: boolean;
    notes: boolean;
    scientificName: boolean;
  };
  onUpdate: (rowIndex: number, updates: Partial<ImportRow>) => void;
  isCoral: boolean;
}

function ReviewRow({ row, hasColumn, onUpdate, isCoral }: ReviewRowProps) {
  const categoryValue = row.category?.value ?? '';
  const subcategoryOptions = useMemo(() => {
    if (!isCoral || !categoryValue) return [];
    return getSubcategoriesForCategory(categoryValue);
  }, [isCoral, categoryValue]);

  // Determine category options. For non-coral types, we don't show category.
  const categoryOptions = isCoral ? CORAL_CATEGORY_OPTIONS : [];

  // Live issues derived from current row state (not the stale initial array)
  const liveIssues = useMemo(() => computeRowIssues(row), [row]);

  const isFlagged = (result: AutoFixResult | null): boolean =>
    result?.confidence === 'flagged';

  const flaggedStyle = {
    backgroundColor: 'var(--mantine-color-yellow-0)',
  };

  return (
    <Table.Tr
      style={{
        opacity: row.isSkipped ? 0.5 : 1,
      }}
    >
      {/* Skip checkbox */}
      <Table.Td>
        <Checkbox
          checked={row.isSkipped}
          onChange={(e) =>
            onUpdate(row._rowIndex, { isSkipped: e.currentTarget.checked })
          }
          size="xs"
          aria-label={`Skip row ${row._rowIndex + 1}`}
        />
      </Table.Td>

      {/* Row number */}
      <Table.Td>
        <Group gap={4}>
          <Text size="xs" c="dimmed">
            {row._rowIndex + 1}
          </Text>
          {liveIssues.length > 0 && (
            <Tooltip
              label={liveIssues.join('; ')}
              multiline
              maw={300}
              withArrow
            >
              <IconAlertTriangle
                size={14}
                color="var(--mantine-color-yellow-6)"
              />
            </Tooltip>
          )}
          {row.isDuplicate && (
            <Badge size="xs" variant="light" color="yellow">
              dup
            </Badge>
          )}
        </Group>
      </Table.Td>

      {/* Name */}
      <Table.Td>
        <TextInput
          value={row.name}
          onChange={(e) =>
            onUpdate(row._rowIndex, { name: e.currentTarget.value })
          }
          size="xs"
          variant="unstyled"
          styles={{
            input: {
              borderBottom: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 0,
            },
          }}
        />
      </Table.Td>

      {/* Category */}
      {hasColumn.category && (
        <Table.Td style={isFlagged(row.category) ? flaggedStyle : undefined}>
          <Select
            data={categoryOptions}
            value={categoryValue || null}
            onChange={(val) => {
              const newCategory: AutoFixResult = {
                value: val ?? '',
                originalValue: row.category?.originalValue ?? null,
                confidence: 'exact',
              };
              // Reset subcategory when category changes
              const newSubcats = val ? (CORAL_CATEGORIES[val] ?? []) : [];
              const currentSub = row.subcategory?.value ?? '';
              const subStillValid = newSubcats.includes(currentSub);
              onUpdate(row._rowIndex, {
                category: newCategory,
                subcategory: subStillValid
                  ? row.subcategory
                  : { value: '', originalValue: row.subcategory?.originalValue ?? null, confidence: 'exact' },
              });
            }}
            size="xs"
            clearable
            placeholder="Select..."
          />
          <AutoFixLabel result={row.category} />
        </Table.Td>
      )}

      {/* Subcategory */}
      {hasColumn.subcategory && (
        <Table.Td
          style={isFlagged(row.subcategory) ? flaggedStyle : undefined}
        >
          <Select
            data={subcategoryOptions}
            value={row.subcategory?.value || null}
            onChange={(val) => {
              onUpdate(row._rowIndex, {
                subcategory: {
                  value: val ?? '',
                  originalValue: row.subcategory?.originalValue ?? null,
                  confidence: 'exact',
                },
              });
            }}
            size="xs"
            clearable
            placeholder="Select..."
            searchable
          />
          <AutoFixLabel result={row.subcategory} />
        </Table.Td>
      )}

      {/* Status */}
      {hasColumn.status && (
        <Table.Td style={isFlagged(row.status) ? flaggedStyle : undefined}>
          <Select
            data={STATUS_SELECT_OPTIONS}
            value={row.status?.value || null}
            onChange={(val) => {
              onUpdate(row._rowIndex, {
                status: {
                  value: val ?? '',
                  originalValue: row.status?.originalValue ?? null,
                  confidence: 'exact',
                },
              });
            }}
            size="xs"
            clearable
            placeholder="Select..."
          />
          <AutoFixLabel result={row.status} />
        </Table.Td>
      )}

      {/* Price */}
      {hasColumn.price && (
        <Table.Td>
          <NumberInput
            value={row.price ?? ''}
            onChange={(val) =>
              onUpdate(row._rowIndex, {
                price: typeof val === 'number' ? val : null,
              })
            }
            size="xs"
            min={0}
            decimalScale={2}
            variant="unstyled"
            styles={{
              input: {
                borderBottom: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 0,
              },
            }}
            placeholder="-"
          />
        </Table.Td>
      )}

      {/* Date */}
      {hasColumn.date && (
        <Table.Td>
          <DateInput
            value={row.acquisitionDate ?? undefined}
            onChange={(val) =>
              onUpdate(row._rowIndex, {
                acquisitionDate: val || null,
              })
            }
            size="xs"
            clearable
            valueFormat="YYYY-MM-DD"
            placeholder="YYYY-MM-DD"
          />
        </Table.Td>
      )}

      {/* Vendor */}
      {hasColumn.vendor && (
        <Table.Td>
          <TextInput
            value={row.vendor ?? ''}
            onChange={(e) =>
              onUpdate(row._rowIndex, {
                vendor: e.currentTarget.value || null,
              })
            }
            size="xs"
            variant="unstyled"
            styles={{
              input: {
                borderBottom: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 0,
              },
            }}
            placeholder="-"
          />
        </Table.Td>
      )}

      {/* Scientific Name */}
      {hasColumn.scientificName && (
        <Table.Td>
          <TextInput
            value={row.scientificName ?? ''}
            onChange={(e) =>
              onUpdate(row._rowIndex, {
                scientificName: e.currentTarget.value || null,
              })
            }
            size="xs"
            variant="unstyled"
            styles={{
              input: {
                borderBottom: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 0,
                fontStyle: 'italic',
              },
            }}
            placeholder="-"
          />
        </Table.Td>
      )}

      {/* Notes */}
      {hasColumn.notes && (
        <Table.Td>
          <TextInput
            value={row.notes ?? ''}
            onChange={(e) =>
              onUpdate(row._rowIndex, {
                notes: e.currentTarget.value || null,
              })
            }
            size="xs"
            variant="unstyled"
            styles={{
              input: {
                borderBottom: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 0,
              },
            }}
            placeholder="-"
          />
        </Table.Td>
      )}
    </Table.Tr>
  );
}
